import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Download } from "lucide-react";
import { exportToCsv } from "@/lib/exportCsv";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ApplicationsDetailDialog({ open, onOpenChange }: Props) {
  const { data: applications = [], isLoading } = useQuery({
    queryKey: ["dashboard-applications-detail"],
    queryFn: async () => {
      const { data } = await supabase
        .from("applications")
        .select("id, applicant_name, program_applied, status, created_at, email")
        .order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: open,
  });

  const pending = applications.filter((a) => a.status === "pending");
  const approved = applications.filter((a) => a.status === "approved");
  const rejected = applications.filter((a) => a.status === "rejected");

  const handleExport = () => {
    const headers = ["Name", "Email", "Program", "Status", "Date"];
    const rows = applications.map((a) => [a.applicant_name, a.email, a.program_applied, a.status, format(new Date(a.created_at), "yyyy-MM-dd")]);
    exportToCsv("applications.csv", headers, rows);
  };

  const statusColor = (s: string) =>
    s === "approved" ? "default" : s === "rejected" ? "destructive" : "secondary";

  const renderList = (items: typeof applications) =>
    items.length === 0 ? (
      <p className="text-sm text-muted-foreground text-center py-6">No applications</p>
    ) : (
      <div className="space-y-2 max-h-[350px] overflow-y-auto">
        {items.map((app) => (
          <div key={app.id} className="flex items-center justify-between p-3 border rounded-lg">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{app.applicant_name}</p>
              <p className="text-xs text-muted-foreground truncate">
                {app.program_applied} · {format(new Date(app.created_at), "dd MMM yyyy")}
              </p>
            </div>
            <Badge variant={statusColor(app.status)} className="shrink-0 capitalize">{app.status}</Badge>
          </div>
        ))}
      </div>
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="flex flex-row items-center justify-between gap-2">
          <div>
            <DialogTitle>Applications Overview</DialogTitle>
            <DialogDescription>Detailed list of all applications by status</DialogDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={applications.length === 0} className="mr-8 shrink-0">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground text-center py-6">Loading…</p>
        ) : (
          <Tabs defaultValue="pending">
            <TabsList className="w-full">
              <TabsTrigger value="pending" className="flex-1">Pending ({pending.length})</TabsTrigger>
              <TabsTrigger value="approved" className="flex-1">Approved ({approved.length})</TabsTrigger>
              <TabsTrigger value="rejected" className="flex-1">Rejected ({rejected.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="pending">{renderList(pending)}</TabsContent>
            <TabsContent value="approved">{renderList(approved)}</TabsContent>
            <TabsContent value="rejected">{renderList(rejected)}</TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
